/**
 * Rate Limiter Utility
 * Implements client-side rate limiting to prevent 429 errors
 */

interface RequestQueue {
  url: string;
  resolve: (value: any) => void;
  reject: (error: any) => void;
  requestFn: () => Promise<any>;
}

class RateLimiter {
  private queue: RequestQueue[] = [];
  private processing = false;
  private requestCount = 0;
  private windowStart = Date.now();
  private readonly maxRequests: number;
  private readonly windowMs: number;
  private readonly delayBetweenRequests: number;

  constructor(
    maxRequests = 100, // Max requests per window
    windowMs = 60000, // 1 minute window
    delayBetweenRequests = 50 // 50ms delay between requests
  ) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.delayBetweenRequests = delayBetweenRequests;
  }

  /**
   * Add a request to the rate-limited queue
   */
  async enqueue<T>(url: string, requestFn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({
        url,
        resolve,
        reject,
        requestFn,
      });

      if (!this.processing) {
        this.processQueue();
      }
    });
  }

  /**
   * Process the request queue with rate limiting
   */
  private async processQueue(): Promise<void> {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      // Reset window if needed
      const now = Date.now();
      if (now - this.windowStart >= this.windowMs) {
        this.requestCount = 0;
        this.windowStart = now;
      }

      // Check if we've hit the rate limit
      if (this.requestCount >= this.maxRequests) {
        const waitTime = this.windowMs - (now - this.windowStart);
        console.warn(`Rate limit reached. Waiting ${waitTime}ms before continuing...`);
        await this.delay(waitTime);
        this.requestCount = 0;
        this.windowStart = Date.now();
      }

      const request = this.queue.shift();
      if (!request) break;

      try {
        // Add delay between requests to prevent overwhelming the server
        if (this.requestCount > 0) {
          await this.delay(this.delayBetweenRequests);
        }

        const result = await request.requestFn();
        this.requestCount++;
        request.resolve(result);
      } catch (error) {
        // Handle 429 errors with exponential backoff
        if (this.is429Error(error)) {
          console.warn('429 error detected, implementing backoff strategy');
          await this.handleRateLimitError(request, error);
        } else {
          request.reject(error);
        }
      }
    }

    this.processing = false;
  }

  /**
   * Handle 429 errors with exponential backoff
   */
  private async handleRateLimitError(request: RequestQueue, error: any): Promise<void> {
    const retryAfter = this.getRetryAfterHeader(error);
    const backoffTime = retryAfter || this.calculateBackoffTime();

    console.warn(`Rate limited. Retrying after ${backoffTime}ms`);
    await this.delay(backoffTime);

    // Reset rate limiter state
    this.requestCount = 0;
    this.windowStart = Date.now();

    // Retry the request
    try {
      const result = await request.requestFn();
      request.resolve(result);
    } catch (retryError) {
      request.reject(retryError);
    }
  }

  /**
   * Check if error is a 429 rate limit error
   */
  private is429Error(error: any): boolean {
    return (
      error?.response?.status === 429 ||
      error?.status === 429 ||
      error?.message?.includes('429') ||
      error?.message?.toLowerCase().includes('too many requests')
    );
  }

  /**
   * Extract Retry-After header from 429 response
   */
  private getRetryAfterHeader(error: any): number | null {
    const retryAfter = error?.response?.headers?.['retry-after'];
    if (retryAfter) {
      const seconds = parseInt(retryAfter, 10);
      return isNaN(seconds) ? null : seconds * 1000; // Convert to milliseconds
    }
    return null;
  }

  /**
   * Calculate exponential backoff time
   */
  private calculateBackoffTime(): number {
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const backoff = Math.min(baseDelay * Math.pow(2, this.requestCount), maxDelay);
    return backoff + Math.random() * 1000; // Add jitter
  }

  /**
   * Delay utility
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get current queue status
   */
  getStatus(): {
    queueLength: number;
    requestCount: number;
    windowStart: number;
    processing: boolean;
  } {
    return {
      queueLength: this.queue.length,
      requestCount: this.requestCount,
      windowStart: this.windowStart,
      processing: this.processing,
    };
  }

  /**
   * Clear the queue (use with caution)
   */
  clearQueue(): void {
    this.queue.forEach((request) => {
      request.reject(new Error('Request cancelled due to queue clear'));
    });
    this.queue = [];
    this.processing = false;
  }
}

// Create a singleton instance
export const rateLimiter = new RateLimiter();

// Export the class for custom instances
export { RateLimiter };
